// Third-party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import debounce from 'lodash/debounce';
import { Redirect } from 'react-router-dom';

// Components
import { OverlappingBackground, Header } from '../../components/layout';
import { PrimaryButton } from '../../components/buttons';
import { AnnouncementsTable } from '../../components/tables';
import { AnnouncementDetailsModal, CreateAnnouncementModal, DeleteAnnouncementModal } from '../../components/modals';
import { LoadingSpinner } from '../../components/common';
import { SearchInput } from '../../components/inputs';
import { Typography } from '@capintel/component-lib/typography';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { Announcement } from '../../../redux/modules/types/announcements';
import { getAnnouncements } from '../../../redux/modules/announcements/admin';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { PERMISSIONS, userHasPermission } from '../../../utils/permissions';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { ALLOWED_HTML_TAGS } from '@/views/components/i18n/FormattedMessageV2';

type DispatchProps = {
  getAnnouncements: (query?: string, nextToken?: string) => Promise<Announcement[]>;
};

type StateProps = {
  announcements: Announcement[];
  nextAnnouncementsToken?: string;
  user?: Nullable<User>;
};

type Props = DispatchProps & StateProps;

type State = {
  initialized: boolean;
  searchQuery?: string;
  selectedAnnouncementId?: string;
  showAddAnnouncementModal: boolean;
  showAnnouncementDetailsModal: boolean;
  showDeleteAnnouncementModal: boolean;
  searching: boolean;
};

class Announcements extends Component<Props, State> {
  readonly state: State = {
    initialized: false,
    searchQuery: '',
    showAddAnnouncementModal: false,
    showAnnouncementDetailsModal: false,
    showDeleteAnnouncementModal: false,
    searching: false,
  };

  componentDidMount() {
    this.props.getAnnouncements().then(() => this.setState({ initialized: true }));
  }

  onSearchAnnouncements = debounce((searchQuery: string) => {
    this.setState({ searchQuery, searching: true });
    this.props.getAnnouncements(searchQuery, undefined).then(() => {
      this.setState({ searching: false });
    });
  }, 250);

  onLoadMore = () => {
    const { searchQuery } = this.state;
    const { nextAnnouncementsToken } = this.props;

    this.props.getAnnouncements(searchQuery, nextAnnouncementsToken);
  };

  handleTableRowClick = (announcementId: string) => {
    this.setState({
      selectedAnnouncementId: announcementId,
      showAnnouncementDetailsModal: true,
    });
  };

  handleDeleteRow = (announcementId: string) => {
    this.setState({
      selectedAnnouncementId: announcementId,
      showDeleteAnnouncementModal: true,
    });
  };

  render() {
    const { announcements, nextAnnouncementsToken, user } = this.props;
    const { initialized, selectedAnnouncementId, showAddAnnouncementModal, showAnnouncementDetailsModal, showDeleteAnnouncementModal, searching } = this.state;

    if (!userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_NOTIFICATION_CENTER)) {
      return <Redirect to="/" />;
    }

    return (
      <>
        <Header />
        <OverlappingBackground />
        <main id="content" className="announcements">
          <div className="announcements-header d-flex align-items-center">
            <h1 className="h3 title">
              <FormattedMessage id="AnnouncementsAdmin.PageTitle" />
            </h1>
          </div>

          <div className="announcements-content flex-fill">
            <div className="announcements-subheader">
              <Typography as={ALLOWED_HTML_TAGS.H2} variant="headerSm">
                {formatMessage('AnnouncementsAdmin.SubTitle')}
              </Typography>
              <div className="announcements-actions">
                <SearchInput
                  key="notifications"
                  loading={searching}
                  placeholder={formatMessage('AnnouncementsAdmin.SearchPlaceholder')}
                  onChange={this.onSearchAnnouncements}
                />

                <PrimaryButton onClick={() => this.setState({ showAddAnnouncementModal: true })}>
                  <i className="icon-plus" />
                  <FormattedMessage id="AnnouncementsAdmin.CreateButton" />
                </PrimaryButton>
              </div>
            </div>

            {(!initialized || searching) && <LoadingSpinner />}

            {initialized && !searching && (
              <AnnouncementsTable
                announcements={announcements}
                noDataText={formatMessage('AnnouncementsAdmin.AnnouncementsTable.NoRows')}
                hasMore={nextAnnouncementsToken !== null}
                onDelete={this.handleDeleteRow}
                onRowClick={this.handleTableRowClick}
              />
            )}

            {!!nextAnnouncementsToken && initialized && !searching && <Waypoint onEnter={this.onLoadMore} />}
          </div>
        </main>
        <CreateAnnouncementModal show={showAddAnnouncementModal} onCancel={() => this.setState({ showAddAnnouncementModal: false })} />
        <AnnouncementDetailsModal
          announcementId={selectedAnnouncementId}
          show={showAnnouncementDetailsModal}
          onCancel={() => this.setState({ showAnnouncementDetailsModal: false })}
          onClosed={() => this.setState({ selectedAnnouncementId: undefined })}
        />
        <DeleteAnnouncementModal
          announcementId={selectedAnnouncementId}
          onCancel={() => this.setState({ showDeleteAnnouncementModal: false })}
          onClosed={() => this.setState({ selectedAnnouncementId: undefined })}
          show={showDeleteAnnouncementModal}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  announcements: state.announcementsAdmin.announcements,
  nextAnnouncementsToken: state.announcementsAdmin.nextAnnouncementsToken,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  getAnnouncements,
})(Announcements);
